import { FC } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useApi from 'utils/use-api';
import dayjs from 'dayjs';
import TextArea from 'components/core/text-area';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';

interface CommentsSectionProps {
  requestId: number;
}

interface Comment {
  id: number;
  requestId: number;
  userId: number;
  userName: string;
  userEmail: string;
  message: string;
  createdAt: string;
}

const validationSchema = Yup.object({
  message: Yup.string().required('Comment is required').min(1, 'Comment cannot be empty'),
});

const CommentsSection: FC<CommentsSectionProps> = ({ requestId }) => {
  const api = useApi();

  const {
    data: comments,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['access-request-comments', requestId],
    queryFn: async () => {
      const response = await api.get(`/access-requests/${requestId}/comments`);
      return response.data;
    },
  });

  const form = useFormik({
    initialValues: {
      message: '',
    },
    validationSchema,
    onSubmit: async values => {
      await addCommentMutation.mutateAsync(values);
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      const response = await api.post(`/access-requests/${requestId}/comments`, {
        message: values.message,
      });
      return response.data;
    },
    onSuccess: () => {
      form.resetForm();
      refetch();
    },
  });

  const formatDate = (dateString: string) => {
    try {
      return dayjs(dateString).format('MMM DD, YYYY HH:mm');
    } catch {
      return dateString;
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <form
          onSubmit={e => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <div className="space-y-3">
            <TextArea id="message" label="Add a Comment" placeholder="Write your comment here..." required={true} rows={3} {...form} />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={addCommentMutation.isPending}
                className="inline-flex items-center gap-x-2 rounded-md bg-primary-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {addCommentMutation.isPending ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">Loading comments...</p>
          </div>
        ) : comments && comments.length > 0 ? (
          <div className="flow-root">
            <ul className="-mb-8">
              {comments.map((comment: Comment, commentIdx: number) => (
                <li key={comment.id}>
                  <div className="relative pb-8">
                    {commentIdx !== comments.length - 1 ? (
                      <span className="absolute left-5 top-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                    ) : null}
                    <div className="relative flex items-start space-x-3">
                      <div className="relative">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-white font-medium text-sm">
                          {getInitials(comment.userName)}
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div>
                          <div className="text-sm">
                            <span className="font-medium text-gray-900">{comment.userName}</span>
                          </div>
                          <p className="mt-0.5 text-xs text-gray-500">{formatDate(comment.createdAt)}</p>
                        </div>
                        <div className="mt-2 text-sm text-gray-700">
                          <p className="whitespace-pre-wrap">{comment.message}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No comments yet</p>
            <p className="text-xs text-gray-400">Be the first to add a comment</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentsSection;
